/**
 * Render Graph 的节点类型（DESIGN.md §4.4）。
 *
 * 八种节点直接对应 §4.4 的八步合成顺序。顺序是 Photoshop 语义的一部分，
 * 不是实现细节——`fillOpacity` 在效果之前、mask 在效果之后、clip 在混合之前，
 * 换一下位置得到的就是另一个软件的结果。图结构把这个顺序显式化，
 * 让"顺序错了"变成可断言的事情，而不是埋在一长串函数调用里。
 *
 * **本层不认识文档模型。** 分层规则里 gpu 在 document/engine 之下，
 * 所以这里没有 `Layer`、没有 `LayerId`、没有 `TILE` 常量：
 * 图层用不透明字符串标识，tile 尺寸由调度方传入。合成器接收的是一份
 * **已经解析好的合成计划**，而不是文档树——这也让它可以脱离文档单测。
 */

/** 节点 id 的品牌类型。 */
export type NodeId = string & { readonly __brand: 'NodeId' };

export function nodeId(value: string): NodeId {
  if (value === '') throw new RangeError('节点 id 不能为空字符串。');
  return value as NodeId;
}

export type NodeKind =
  | 'SourceTile'
  | 'Transform'
  | 'Mask'
  | 'Adjustment'
  | 'Effect'
  | 'Blend'
  | 'Clip'
  | 'Output';

/** 八种节点。齐备性由 test/gpu/graph.test.ts 对着这份清单断言。 */
export const NODE_KINDS: readonly NodeKind[] = [
  'SourceTile',
  'Transform',
  'Mask',
  'Adjustment',
  'Effect',
  'Blend',
  'Clip',
  'Output',
];

interface NodeBase {
  readonly id: NodeId;
  readonly kind: NodeKind;
  /** 上游节点。顺序有意义：Blend 的 [背景, 源] 反过来结果就反了。 */
  readonly inputs: readonly NodeId[];
}

/**
 * 取图层的一块像素。
 *
 * 它是图里唯一的叶子，也是唯一**不产生 draw call** 的节点——
 * 它只是把一张已有纹理绑上去。把这一点建模出来，是为了让"每块要提交多少次 draw"
 * 可以直接从图上算出来，而不是等提交到 GPU 才知道。
 */
export interface SourceTileNode extends NodeBase {
  readonly kind: 'SourceTile';
  readonly inputs: readonly [];
  /** 图层标识。本层不解释它的含义，只用于定位纹理。 */
  readonly layer: string;
}

/** 图层局部空间 → 文档空间的仿射变换。六元组按 Mat3 的 a,b,c,d,e,f 排列。 */
export interface TransformNode extends NodeBase {
  readonly kind: 'Transform';
  readonly inputs: readonly [NodeId];
  readonly matrix: readonly [number, number, number, number, number, number];
}

/** 与遮罩相乘。两个输入：[被遮罩的像素, 遮罩]。 */
export interface MaskNode extends NodeBase {
  readonly kind: 'Mask';
  readonly inputs: readonly [NodeId, NodeId];
}

/** 调整图层（色阶/曲线/色相…）。参数留给具体实现，本层只关心它在图里的位置。 */
export interface AdjustmentNode extends NodeBase {
  readonly kind: 'Adjustment';
  readonly inputs: readonly [NodeId];
  readonly adjustment: string;
  readonly params: Readonly<Record<string, number>>;
}

/**
 * 图层样式。
 *
 * `placement` 不是装饰性字段：§4.4 明确"内部效果与外部效果的合成位置不同"。
 * 内阴影在图层 alpha 之内合成，投影在其外——同一个 Effect 节点，
 * 放错位置就会画到不该画的地方。
 */
export interface EffectNode extends NodeBase {
  readonly kind: 'Effect';
  readonly inputs: readonly [NodeId];
  readonly effect: string;
  readonly placement: 'inner' | 'outer';
}

/** 混合进背景。两个输入：[背景, 源]。 */
export interface BlendNode extends NodeBase {
  readonly kind: 'Blend';
  readonly inputs: readonly [NodeId, NodeId];
  readonly blendMode: string;
  /** 0–1。 */
  readonly opacity: number;
  /** 线性空间还是 sRGB 空间混合，按 §4.4 作为 uniform 传入，不硬编码。 */
  readonly linearSpace: boolean;
}

/** 剪贴：与下方基底图层的 alpha 相交。两个输入：[源, 基底]。 */
export interface ClipNode extends NodeBase {
  readonly kind: 'Clip';
  readonly inputs: readonly [NodeId, NodeId];
}

/** 输出到视口。一张图有且只有一个。 */
export interface OutputNode extends NodeBase {
  readonly kind: 'Output';
  readonly inputs: readonly [NodeId];
}

export type RenderNode =
  | SourceTileNode
  | TransformNode
  | MaskNode
  | AdjustmentNode
  | EffectNode
  | BlendNode
  | ClipNode
  | OutputNode;

/** 每种节点要求的输入个数。图在加入节点时按这张表校验。 */
export const NODE_ARITY: Readonly<Record<NodeKind, number>> = {
  SourceTile: 0,
  Transform: 1,
  Mask: 2,
  Adjustment: 1,
  Effect: 1,
  Blend: 2,
  Clip: 2,
  Output: 1,
};

/**
 * 该节点是否产生一次 draw call。
 *
 * 只有 SourceTile 不产生——它是绑纹理，不是画东西。每块的 draw 数由此算出，
 * 分批调度靠的就是这个数字（见 schedule.ts）。
 */
export function producesDraw(kind: NodeKind): boolean {
  return kind !== 'SourceTile';
}
